import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { applyCompetition, fetchTournaments } from '../api/competitionApplication';
import { getApiErrorMessage } from '../api/client';
import { GRADE_OPTIONS } from '../constants/gradeOptions';
import { useI18n } from '../i18n';
import PrivacyAgreement from './PrivacyAgreement';
import SelectionSummary from './SelectionSummary';
import { buildApplicationPayload } from '../utils/applicationPayload';
import { formatEventDate, normalizeCompetitionList } from '../utils/competitionList';
import {
  formatPhoneInput,
  getBirthdateInputBounds,
  validateBirthdate,
  validateHeight,
  validatePhone,
} from '../utils/registrationValidation';

const STEPS = [
  { num: 1, labelKey: 'registration.steps.tournament' },
  { num: 2, labelKey: 'registration.steps.affiliation' },
  { num: 3, labelKey: 'registration.steps.category' },
];

// 목록이 비었을 때 (번역은 렌더링 시점에)
const EMPTY_COMPETITIONS = { empty: true };

const INITIAL_FORM = {
  tournament: '',
  affiliation: '',
  dojo: '',
  coachName: '',
  name: '',
  phone: '',
  guardianPhone: '',
  birthdate: '',
  gender: '',
  grade: '',
  height: '',
};

function RegistrationForm() {
  const { t } = useI18n();
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [visibleStep, setVisibleStep] = useState(1);
  const [lockedThroughStep, setLockedThroughStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [privacyAgreed, setPrivacyAgreed] = useState(false);
  const [competitions, setCompetitions] = useState([]);
  const [competitionsLoading, setCompetitionsLoading] = useState(true);
  // null | EMPTY_COMPETITIONS | { error } — 문구는 렌더링 시점에 현재 언어로 만든다.
  const [competitionsError, setCompetitionsError] = useState(null);
  // const [search, setSearch] = useState('');
  const stepRefs = useRef({});

  const loadCompetitions = useCallback(async () => {
    setCompetitionsLoading(true);
    setCompetitionsError(null);
    try {
      const data = await fetchTournaments();
      const list = normalizeCompetitionList(data);
      if (list.length === 0) {
        setCompetitionsError(EMPTY_COMPETITIONS);
      }
      setCompetitions(list);
    } catch (error) {
      setCompetitionsError({ error });
      setCompetitions([]);
    } finally {
      setCompetitionsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompetitions();
  }, [loadCompetitions]);

  // useEffect(() => {
  //   const timer = setTimeout(() => {
  //     loadCompetitions(search);
  //   }, 300);
  //   return () => clearTimeout(timer);
  // }, [search, loadCompetitions]);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updatePhoneField = (field, value) => {
    updateField(field, formatPhoneInput(value));
  };

  const birthdateBounds = useMemo(() => getBirthdateInputBounds(), []);

  const scrollToStep = (step) => {
    requestAnimationFrame(() => {
      stepRefs.current[step]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const handleNext = (fromStep) => {
    if (fromStep === 1) {
      if (!formData.tournament) {
        alert(t('registration.alerts.selectTournament'));
        return;
      }
    }

    if (fromStep === 2) {
      const affiliation = formData.affiliation.trim();
      const dojo = formData.dojo.trim();
      const coachName = formData.coachName.trim();
      const name = formData.name.trim();
      const phone = formData.phone.trim();
      const guardianPhone = formData.guardianPhone.trim();

      if (!affiliation || !dojo || !coachName || !name) {
        alert(t('registration.alerts.requiredAffiliation'));
        return;
      }
      if (!phone || !guardianPhone) {
        alert(t('registration.alerts.requiredPhones'));
        return;
      }

      const phoneCheck = validatePhone(phone, t('registration.fields.phone'));
      if (!phoneCheck.valid) {
        alert(phoneCheck.message);
        return;
      }
      const guardianCheck = validatePhone(guardianPhone, t('registration.fields.guardianPhone'));
      if (!guardianCheck.valid) {
        alert(guardianCheck.message);
        return;
      }
    }

    setLockedThroughStep(fromStep);
    const nextStep = fromStep + 1;
    setVisibleStep(nextStep);
    scrollToStep(nextStep);
  };

  const handlePrev = (fromStep) => {
    const prevStep = fromStep - 1;
    setVisibleStep(prevStep);
    scrollToStep(prevStep);
  };

  const handleSubmit = async () => {
    if (!formData.gender) {
      alert(t('registration.alerts.selectGender'));
      return;
    }

    if (!formData.grade) {
      alert(t('registration.alerts.selectGrade'));
      return;
    }

    const birthCheck = validateBirthdate(formData.birthdate);
    if (!birthCheck.valid) {
      alert(birthCheck.message);
      return;
    }

    const heightCheck = validateHeight(formData.height);
    if (!heightCheck.valid) {
      alert(heightCheck.message);
      return;
    }

    const phoneCheck = validatePhone(formData.phone, t('registration.fields.phone'));
    if (!phoneCheck.valid) {
      alert(phoneCheck.message);
      return;
    }
    const guardianCheck = validatePhone(formData.guardianPhone, t('registration.fields.guardianPhone'));
    if (!guardianCheck.valid) {
      alert(guardianCheck.message);
      return;
    }

    if (!privacyAgreed) {
      alert(t('registration.alerts.agreePrivacy'));
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildApplicationPayload(formData, competitions);
      await applyCompetition(payload);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      alert(getApiErrorMessage(error, t('registration.alerts.submitError')));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetApplication = () => {
    setSubmitted(false);
    setFormData(INITIAL_FORM);
    setVisibleStep(1);
    setLockedThroughStep(0);
    setPrivacyAgreed(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const selectedTournament = useMemo(
    () => competitions.find((t) => t.value === formData.tournament),
    [competitions, formData.tournament]
  );

  const tournamentSummaryItems = useMemo(() => {
    if (!selectedTournament) return [];
    return [
      { label: t('registration.summary.tournamentName'), value: selectedTournament.name },
      ...(selectedTournament.location
        ? [{ label: t('registration.summary.location'), value: selectedTournament.location }]
        : []),
      ...(selectedTournament.eventAt
        ? [
            {
              label: t('registration.summary.eventDate'),
              value: formatEventDate(selectedTournament.eventAt),
            },
          ]
        : []),
    ];
  }, [selectedTournament, t]);

  const affiliationSummaryItems = useMemo(
    () => [
      { label: t('registration.summary.affiliation'), value: formData.affiliation.trim() },
      { label: t('registration.summary.dojo'), value: formData.dojo.trim() },
      { label: t('registration.summary.coachName'), value: formData.coachName.trim() },
      { label: t('registration.summary.name'), value: formData.name.trim() },
      { label: t('registration.summary.phone'), value: formData.phone.trim() },
      { label: t('registration.summary.guardianPhone'), value: formData.guardianPhone.trim() },
    ],
    [formData, t]
  );

  let competitionsErrorMessage = null;
  if (competitionsError?.empty) {
    competitionsErrorMessage = t('registration.step1.empty');
  } else if (competitionsError) {
    competitionsErrorMessage = getApiErrorMessage(
      competitionsError.error,
      t('registration.step1.loadError')
    );
  }

  const isStep1Locked = lockedThroughStep >= 1 && visibleStep > 1;
  const isStep2Locked = lockedThroughStep >= 2 && visibleStep > 2;

  if (submitted) {
    return (
      <div className="container container-complete">
        <div className="card complete-card">
          <div className="complete-icon">✓</div>
          <h2 className="complete-title">
            {t('registration.complete.titleLine1')}
            <br />
            {t('registration.complete.titleLine2')}
          </h2>
          <p className="complete-subtitle">
            {t('registration.complete.subtitleLine1')}
            <br />
            {t('registration.complete.subtitleLine2')}
          </p>
          <button type="button" className="btn btn-reset-application" onClick={handleResetApplication}>
            {t('registration.buttons.reset')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="page-intro">
        <p className="page-eyebrow">Tournament Registration</p>
        <h2 className="page-title">{t('registration.intro.title')}</h2>
        <p className="page-desc">{t('registration.intro.desc')}</p>
      </div>

      <div className="progress-wrap">
        <nav className="stepper" aria-label={t('registration.stepperAria')}>
          {STEPS.map((step) => (
            <div
              key={step.num}
              className={`stepper-item${
                visibleStep === step.num ? ' current' : ''
              }${visibleStep > step.num ? ' done' : ''}`}
            >
              <div className="stepper-node">
                {visibleStep > step.num ? (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path
                      d="M2.5 7L5.5 10L11.5 4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  step.num
                )}
              </div>
              <span className="stepper-label">{t(step.labelKey)}</span>
            </div>
          ))}
        </nav>
      </div>

      <div className="steps-stack">
        <div className="card" ref={(el) => { stepRefs.current[1] = el; }}>
          <div className="step-label">
            <span className="step-number">01</span>
            <span className="step-tag">TOURNAMENT</span>
          </div>
          <h2 className="step-title">
            {t('registration.step1.titleLine1')}
            <br />
            {t('registration.step1.titleLine2')}
          </h2>
          <p className="step-desc">
            {isStep1Locked
              ? t('registration.step1.lockedDesc')
              : t('registration.step1.desc')}
          </p>
          {isStep1Locked ? (
            <SelectionSummary title={t('registration.summary.tournamentTitle')} items={tournamentSummaryItems} />
          ) : (
            <>
          {/* 대회명 검색 (잠시 비활성화)
          <div className="form-group">
            <label className="form-label" htmlFor="tournamentSearch">
              대회명 검색
            </label>
            <input
              id="tournamentSearch"
              type="search"
              className="form-input"
              placeholder="대회명으로 검색"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          */}
          <div className="tournament-options">
            {competitionsLoading && (
              <div className="list-status">
                <div className="skeleton-card" />
                <div className="skeleton-card" />
                <div className="skeleton-card" />
              </div>
            )}
            {!competitionsLoading && competitionsError && (
              <div className="list-status list-status-error">
                <p>{competitionsErrorMessage}</p>
                <button
                  type="button"
                  className="btn btn-retry"
                  onClick={loadCompetitions}
                >
                  {t('common.retry')}
                </button>
              </div>
            )}
            {!competitionsLoading &&
              !competitionsError &&
              competitions.map((tournament) => (
                <div
                  key={tournament.id}
                  role="button"
                  tabIndex={0}
                  className={`tournament-card${
                    formData.tournament === tournament.value ? ' selected' : ''
                  }`}
                  onClick={() => updateField('tournament', tournament.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      updateField('tournament', tournament.value);
                    }
                  }}
                >
                  <div className="tournament-card-inner">
                    <div className="tournament-info">
                      <div className="tournament-name">{tournament.name}</div>
                      <div className="tournament-meta">
                        {tournament.location && (
                          <span className="meta-chip">{tournament.location}</span>
                        )}
                        {tournament.eventAt && (
                          <span className="meta-chip">{formatEventDate(tournament.eventAt)}</span>
                        )}
                      </div>
                    </div>
                    <div className="tournament-check" aria-hidden="true" />
                  </div>
                </div>
              ))}
          </div>
            </>
          )}
          {visibleStep === 1 && !isStep1Locked && (
            <div className="button-group">
              <button
                type="button"
                className="btn btn-next"
                onClick={() => handleNext(1)}
                disabled={competitionsLoading || !!competitionsError || competitions.length === 0}
              >
                {t('registration.buttons.next')}
              </button>
            </div>
          )}
        </div>

        {visibleStep >= 2 && (
          <div className="card" ref={(el) => { stepRefs.current[2] = el; }}>
            <div className="step-label">
              <span className="step-number">02</span>
              <span className="step-tag">AFFILIATION</span>
            </div>
            <h2 className="step-title">
              {t('registration.step2.titleLine1')}
              <br />
              {t('registration.step2.titleLine2')}
            </h2>
            <p className="step-desc">
              {isStep2Locked
                ? t('registration.step2.lockedDesc')
                : t('registration.step2.desc')}
            </p>

            {isStep1Locked && (
              <SelectionSummary title={t('registration.summary.tournamentTitle')} items={tournamentSummaryItems} />
            )}

            {isStep2Locked ? (
              <SelectionSummary title={t('registration.summary.affiliationTitle')} items={affiliationSummaryItems} />
            ) : (
              <>
            <div className="form-group">
              <label className="form-label" htmlFor="affiliation">
                {t('registration.fields.affiliation')} <span className="required">*</span>
              </label>
              <input
                id="affiliation"
                type="text"
                className="form-input"
                placeholder={t('registration.placeholders.affiliation')}
                value={formData.affiliation}
                onChange={(e) => updateField('affiliation', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="dojo">
                {t('registration.fields.dojo')} <span className="required">*</span>
              </label>
              <input
                id="dojo"
                type="text"
                className="form-input"
                placeholder={t('registration.placeholders.dojo')}
                value={formData.dojo}
                onChange={(e) => updateField('dojo', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="coachName">
                {t('registration.fields.coachName')} <span className="required">*</span>
              </label>
              <input
                id="coachName"
                type="text"
                className="form-input"
                placeholder={t('registration.placeholders.coachName')}
                value={formData.coachName}
                onChange={(e) => updateField('coachName', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="name">
                {t('registration.fields.name')} <span className="required">*</span>
              </label>
              <input
                id="name"
                type="text"
                className="form-input"
                placeholder={t('registration.placeholders.name')}
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label" htmlFor="phone">
                  {t('registration.fields.phone')} <span className="required">*</span>
                </label>
                <input
                  id="phone"
                  type="tel"
                  inputMode="numeric"
                  className="form-input"
                  placeholder="010-1234-5678"
                  value={formData.phone}
                  onChange={(e) => updatePhoneField('phone', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="guardianPhone">
                  {t('registration.fields.guardianPhone')} <span className="required">*</span>
                </label>
                <input
                  id="guardianPhone"
                  type="tel"
                  inputMode="numeric"
                  className="form-input"
                  placeholder="010-1234-5678"
                  value={formData.guardianPhone}
                  onChange={(e) => updatePhoneField('guardianPhone', e.target.value)}
                />
              </div>
            </div>
              </>
            )}

            {visibleStep === 2 && !isStep2Locked && (
              <div className="button-group">
                <button type="button" className="btn btn-prev" onClick={() => handlePrev(2)}>
                  {t('registration.buttons.prev')}
                </button>
                <button type="button" className="btn btn-next" onClick={() => handleNext(2)}>
                  {t('registration.buttons.next')}
                </button>
              </div>
            )}
          </div>
        )}

        {visibleStep >= 3 && (
          <div className="card" ref={(el) => { stepRefs.current[3] = el; }}>
            <div className="step-label">
              <span className="step-number">03</span>
              <span className="step-tag">CATEGORY</span>
            </div>
            <h2 className="step-title">
              {t('registration.step3.titleLine1')}
              <br />
              {t('registration.step3.titleLine2')}
            </h2>
            <p className="step-desc">
              {t('registration.step3.desc')}
            </p>

            <div className="step-previous-selections">
              {isStep1Locked && (
                <SelectionSummary title={t('registration.summary.tournamentTitle')} items={tournamentSummaryItems} />
              )}
              {isStep2Locked && (
                <SelectionSummary title={t('registration.summary.affiliationTitle')} items={affiliationSummaryItems} />
              )}
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="birthdate">
                {t('registration.fields.birthdate')} <span className="required">*</span>
              </label>
              <input
                id="birthdate"
                type="date"
                className="form-input"
                value={formData.birthdate}
                min={birthdateBounds.min}
                max={birthdateBounds.max}
                onChange={(e) => updateField('birthdate', e.target.value)}
              />
            </div>
            <div className="form-group">
              <span className="form-label">
                {t('registration.fields.gender')} <span className="required">*</span>
              </span>
              <div className="gender-group">
                <div
                  role="button"
                  tabIndex={0}
                  className={`gender-option${formData.gender === '남' ? ' selected' : ''}`}
                  onClick={() => updateField('gender', '남')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      updateField('gender', '남');
                    }
                  }}
                >
                  {t('registration.gender.maleOption')}
                </div>
                <div
                  role="button"
                  tabIndex={0}
                  className={`gender-option female${
                    formData.gender === '여' ? ' selected' : ''
                  }`}
                  onClick={() => updateField('gender', '여')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      updateField('gender', '여');
                    }
                  }}
                >
                  {t('registration.gender.femaleOption')}
                </div>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="grade">
                {t('registration.fields.grade')} <span className="required">*</span>
              </label>
              <select
                id="grade"
                className="form-select"
                value={formData.grade}
                onChange={(e) => updateField('grade', e.target.value)}
              >
                {GRADE_OPTIONS.map((option) => (
                  <option key={option.value || 'placeholder'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="height">
                {t('registration.fields.height')} <span className="required">*</span>
              </label>
              <input
                id="height"
                type="number"
                className="form-input"
                placeholder={t('registration.placeholders.height')}
                min={80}
                max={250}
                value={formData.height}
                onChange={(e) => updateField('height', e.target.value)}
              />
            </div>

            <PrivacyAgreement
              checked={privacyAgreed}
              onChange={setPrivacyAgreed}
              disabled={submitting}
            />

            <div className="button-group">
              <button type="button" className="btn btn-prev" onClick={() => handlePrev(3)}>
                {t('registration.buttons.prev')}
              </button>
              <button
                type="button"
                className="btn btn-submit"
                onClick={handleSubmit}
                disabled={submitting || !privacyAgreed}
              >
                {submitting ? t('registration.buttons.submitting') : t('registration.buttons.submit')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RegistrationForm;
